// ctrl + K command palette. Two sources share one keyboard-driven list: local
// nav destinations and actions filtered in memory, and entity suggestions from
// GET /api/v1/search/mine (debounced, cancellable — see useGlobalSearch), which
// is scoped server-side, so nothing rendered here needs its own access check.
//
// Entities rank above "Go to" once a query is typed: someone typing a serial
// wants that robot, not a page whose name shares letters. The flat `rows` array
// is what the arrow keys move through — headers render inline but are NOT rows,
// so Enter always has something to run.
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, InputBase, CircularProgress } from "@mui/material";
import {
  LayoutDashboard,
  Bot,
  Building,
  Megaphone,
  Bell,
  User,
  CreditCard,
  Settings,
  Moon,
  Sun,
  Search,
  Layers,
  CircleDot,
  TriangleAlert,
  CornerDownLeft,
} from "lucide-react";
import { useThemeMode } from "../../ThemeModeContext";
import { tokens } from "../../theme";
import useGlobalSearch, { MIN_QUERY_LENGTH } from "../../hooks/useGlobalSearch";

// Keyed by the server's `type` values (searchMine.v1.service.js
// SEARCHABLE_TYPES). An unknown type falls back rather than crashing, so adding
// an entity server-side cannot break this page.
const ENTITY_META = {
  robot: { label: "Robots", icon: Bot },
  venue: { label: "Venues", icon: Building },
  map: { label: "Maps", icon: Layers },
  advertisement: { label: "Advertisements", icon: Megaphone },
  notification: { label: "Notifications", icon: Bell },
};
const ENTITY_FALLBACK = { label: "Results", icon: CircleDot };
const metaFor = (type) => ENTITY_META[type] || ENTITY_FALLBACK;

// Covers robot `status` and advertisement `approvalStatus`; an unknown one
// renders no chip. Safe at module scope because `tokens` values are CSS var()
// references, not resolved colours, so they follow the theme without a re-render.
const STATUS_COLOR = {
  active: tokens.success,
  running: tokens.success,
  approved: tokens.success,
  idle: tokens.success,
  pending: tokens.warning,
  unassigned: tokens.warning,
  expired: tokens.text3,
  inactive: tokens.text3,
  error: tokens.danger,
  rejected: tokens.danger,
};

// Splits `text` around a case-insensitive match of `q` so the matched run can be
// emphasized. Null when there is nothing to highlight.
const splitMatch = (text, q) => {
  if (!q || !text) return null;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return null;
  return [text.slice(0, i), text.slice(i, i + q.length), text.slice(i + q.length)];
};

const Highlight = ({ text, query }) => {
  const parts = splitMatch(text, query);
  if (!parts) return text;
  return (
    <>
      {parts[0]}
      <Box
        component="mark"
        sx={{ background: "transparent", color: tokens.accent, fontWeight: 600, p: 0 }}
      >
        {parts[1]}
      </Box>
      {parts[2]}
    </>
  );
};
Highlight.propTypes = { text: PropTypes.string, query: PropTypes.string };

// Flatten one server group into selectable rows, out of the `rows` memo.
const entityRowsFor = (group, navigate) => {
  const { label } = metaFor(group.type);
  // Group heading carries the real total so a capped group reads "Robots · 12"
  // rather than implying only the 5 shown exist.
  const groupLabel = group.total > group.items.length ? `${label} · ${group.total}` : label;
  return group.items.map((item) => ({
    id: `${item.type}:${item.id}`,
    label: item.title,
    subtitle: item.subtitle,
    icon: metaFor(item.type).icon,
    status: item.meta?.status,
    groupLabel,
    // `route` is resolved server-side — the palette never assembles one itself.
    run: () => navigate(item.route),
  }));
};

const ROW_TEXT_SX = {
  display: "block",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// One row of the palette, extracted so the option markup is not a map inside
// the component's return.
const PaletteRow = ({ row, index, isActive, showHeader, query, onActivate, onRun }) => {
  const Icon = row.icon;
  return (
    // A fragment, not a wrapper Box: role="option" must be a direct child of the
    // listbox, or the rows stop being its options and aria-activedescendant
    // silently breaks.
    <>
      {showHeader ? (
        // A bare div is not a permitted listbox child either. The heading is
        // decorative (each row's label already names its group), so it leaves the
        // a11y tree. Sonar's semantic-element fix does not apply: no HTML element
        // carries "presentation" implicitly.
        <Box // NOSONAR
          role="presentation"
          sx={{
            px: 1.5,
            pt: 1,
            pb: 0.5,
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: tokens.text3,
          }}
        >
          {row.groupLabel}
        </Box>
      ) : null}
      <Box // NOSONAR
        component="button"
        type="button"
        id={`cmdk-row-${index}`}
        data-row-index={index}
        // Combobox pattern: the input keeps focus and owns these via
        // aria-activedescendant. A native <option> cannot carry icons, subtitles
        // or chips, and focusing the rows would drop the typing announcement.
        role="option"
        aria-selected={isActive}
        onClick={() => onRun(index)}
        onMouseEnter={() => onActivate(index)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          width: "100%",
          p: "10px 12px",
          borderRadius: "9px",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          fontSize: 13.5,
          fontFamily: "inherit",
          background: isActive ? tokens.accentSoft : "transparent",
          color: isActive ? tokens.accent : tokens.text,
        }}
      >
        <Icon size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          <Box component="span" sx={ROW_TEXT_SX}>
            <Highlight text={row.label} query={query} />
          </Box>
          {row.subtitle ? (
            <Box component="span" sx={{ ...ROW_TEXT_SX, fontSize: 11.5, color: tokens.text3 }}>
              <Highlight text={row.subtitle} query={query} />
            </Box>
          ) : null}
        </Box>
        {row.status && STATUS_COLOR[row.status] ? (
          <Box
            component="span"
            sx={{
              flex: "none",
              fontSize: 10.5,
              textTransform: "capitalize",
              color: STATUS_COLOR[row.status],
            }}
          >
            {row.status}
          </Box>
        ) : null}
        {isActive ? <CornerDownLeft size={13} style={{ flexShrink: 0 }} /> : null}
      </Box>
    </>
  );
};
PaletteRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isActive: PropTypes.bool,
  showHeader: PropTypes.bool,
  query: PropTypes.string,
  onActivate: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
};

// Which empty state to show, as a flat sequence of guards: "keep typing" for a
// short query, then a genuine no-results. A first search in flight shows
// neither — the header spinner is the feedback.
const emptyStateText = ({ rowCount, showTooShortHint, loading, isEmpty, q }) => {
  if (rowCount > 0) return null;
  if (showTooShortHint) return "Keep typing to search…";
  if (loading) return null;
  if (!isEmpty && q) return null;
  return q ? `No results for "${q}"` : "No results";
};

// The listbox popup, extracted so the row markup and the empty/error branches
// are not several levels deep inside the panel.
const PaletteResults = ({
  listRef,
  rows,
  active,
  query,
  error,
  loading,
  isEmpty,
  showTooShortHint,
  onActivate,
  onRun,
}) => {
  const emptyText = emptyStateText({
    rowCount: rows.length,
    showTooShortHint,
    loading,
    isEmpty,
    q: query,
  });

  return (
    // <select>/<datalist> cannot render this: the rows carry icons, subtitles
    // and chips, and the input must keep focus while owning them.
    <Box // NOSONAR
      id="cmdk-results"
      ref={listRef}
      role="listbox"
      aria-label="Search results"
      sx={{ p: 1, maxHeight: 420, overflowY: "auto" }}
    >
      {error ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: "10px 12px",
            mb: 0.5,
            borderRadius: "9px",
            background: tokens.dangerSoft,
            color: tokens.danger,
            fontSize: 12.5,
          }}
        >
          <TriangleAlert size={14} /> {error}
        </Box>
      ) : null}

      {rows.map((row, i) => (
        <PaletteRow
          key={row.id}
          row={row}
          index={i}
          isActive={i === active}
          // A header is emitted whenever this row's group differs from the
          // previous row's — so grouping follows the row order exactly.
          showHeader={Boolean(row.groupLabel && rows[i - 1]?.groupLabel !== row.groupLabel)}
          query={query}
          onActivate={onActivate}
          onRun={onRun}
        />
      ))}

      {emptyText ? (
        <Box sx={{ p: 2.5, textAlign: "center", fontSize: 13, color: tokens.text3 }}>
          {emptyText}
        </Box>
      ) : null}
    </Box>
  );
};
PaletteResults.propTypes = {
  listRef: PropTypes.object,
  rows: PropTypes.array.isRequired,
  active: PropTypes.number,
  query: PropTypes.string,
  error: PropTypes.string,
  loading: PropTypes.bool,
  isEmpty: PropTypes.bool,
  showTooShortHint: PropTypes.bool,
  onActivate: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
};

// Arrow keys wrap the flat row list, Enter runs the active row, Escape closes.
// Split out so the branchy part is off the component's complexity budget.
const handlePaletteKey = (e, { rowCount, active, onClose, runAt, setActive }) => {
  if (e.key === "Escape") {
    onClose();
    return;
  }
  if (e.key === "Enter") {
    e.preventDefault();
    runAt(active);
    return;
  }
  const step = { ArrowDown: 1, ArrowUp: -1 }[e.key];
  if (step === undefined) return;
  e.preventDefault();
  setActive((a) => (rowCount ? (a + step + rowCount) % rowCount : 0));
};

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { isDark, toggleMode } = useThemeMode();
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Only search while the palette is open — a closed palette must not keep
  // issuing requests for whatever text was left in the box.
  const { groups, totalResults, loading, error, isEmpty } = useGlobalSearch(query, {
    enabled: open,
    limit: 5,
  });

  // Command list — actions navigate to real routes or toggle theme.
  const commands = useMemo(
    () => [
      {
        id: "nav:dashboard",
        label: "Go to Dashboard",
        icon: LayoutDashboard,
        run: () => navigate("/dashboard"),
      },
      { id: "nav:robots", label: "My Robots", icon: Bot, run: () => navigate("/robots") },
      { id: "nav:venue", label: "Venues", icon: Building, run: () => navigate("/venue") },
      { id: "nav:maps", label: "Maps", icon: Layers, run: () => navigate("/maps") },
      {
        id: "nav:ads",
        label: "Advertisements",
        icon: Megaphone,
        run: () => navigate("/advertisements"),
      },
      {
        id: "nav:notifications",
        label: "Notifications",
        icon: Bell,
        run: () => navigate("/notifications"),
      },
      { id: "nav:account", label: "Account", icon: User, run: () => navigate("/profile") },
      { id: "nav:payment", label: "Payment", icon: CreditCard, run: () => navigate("/payment") },
      { id: "nav:settings", label: "Settings", icon: Settings, run: () => navigate("/settings") },
      {
        id: "action:theme",
        label: isDark ? "Switch to light theme" : "Switch to dark theme",
        icon: isDark ? Sun : Moon,
        run: () => toggleMode(),
      },
    ],
    [navigate, isDark, toggleMode]
  );

  const q = query.trim();

  const filteredCommands = useMemo(() => {
    if (!q) return commands;
    const needle = q.toLowerCase();
    return commands.filter((c) => c.label.toLowerCase().includes(needle));
  }, [q, commands]);

  // One flat list of SELECTABLE rows, each tagged with its group header. Headers
  // are derived at render from `groupLabel` changing between rows, so the index
  // space and the visual grouping cannot drift apart.
  const rows = useMemo(() => {
    const entityRows = groups.flatMap((g) => entityRowsFor(g, navigate));
    const commandRows = filteredCommands.map((c) => ({ ...c, groupLabel: q ? "Go to" : null }));
    return [...entityRows, ...commandRows];
  }, [groups, filteredCommands, navigate, q]);

  // Reset + focus when opened, so the palette never reopens showing the previous
  // query.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      // focus after the element mounts
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Any change to the result set invalidates the highlight — index 3 is a
  // different row now. Snap to the top, which is also the most relevant.
  useEffect(() => {
    setActiveIdx(0);
  }, [query, groups]);

  // Clamp rather than let `activeIdx` dangle past the end when results shrink,
  // which would leave Enter inert.
  useEffect(() => {
    setActiveIdx((a) => (rows.length === 0 ? 0 : Math.min(a, rows.length - 1)));
  }, [rows.length]);

  // Keep the highlighted row in view when moving by keyboard past the visible
  // window; `nearest` avoids a jarring re-centre on every step.
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector(`[data-row-index="${activeIdx}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  const runAt = useCallback(
    (idx) => {
      const row = rows[idx];
      if (!row) return;
      // Close BEFORE navigating, so the panel does not flash stale results from the
      // page being left.
      onClose();
      row.run();
    },
    [rows, onClose]
  );

  const handleKeyDown = (e) =>
    handlePaletteKey(e, {
      rowCount: rows.length,
      active: activeIdx,
      onClose,
      runAt,
      setActive: setActiveIdx,
    });

  if (!open) return null;

  const showTooShortHint = q.length > 0 && q.length < MIN_QUERY_LENGTH;

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "rgba(0,0,0,.5)",
        backdropFilter: "blur(3px)",
        display: "grid",
        placeItems: "start center",
        pt: "12vh",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        sx={{
          width: "min(560px, 92vw)",
          background: tokens.surface,
          border: `1px solid ${tokens.borderStrong}`,
          borderRadius: "14px",
          boxShadow: tokens.shadowLg,
          overflow: "hidden",
          animation: "bq-page-in .14s ease",
        }}
      >
        {/* Search input */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: "14px 16px",
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Search size={18} color={tokens.text3} />
          {/* The input owns the listbox via aria-controls/activedescendant: it
              keeps focus throughout, so the rows are never focused and could not
              otherwise be announced. */}
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search robots, venues, maps, ads…"
            autoComplete="off"
            inputProps={{
              "aria-label": "Search robots, venues, maps, advertisements and notifications",
              role: "combobox",
              "aria-expanded": true,
              "aria-controls": "cmdk-results",
              "aria-activedescendant": rows[activeIdx] ? `cmdk-row-${activeIdx}` : undefined,
            }}
            sx={{ flex: 1, fontSize: 15, color: tokens.text }}
          />
          {/* Spinner replaces the ESC hint while a search is in flight, so the
              pending state is visible without adding a second moving element. */}
          {loading ? (
            <CircularProgress size={14} thickness={5} sx={{ color: tokens.text3 }} />
          ) : (
            <Box
              component="kbd"
              sx={{
                fontSize: 10.5,
                color: tokens.text3,
                border: `1px solid ${tokens.border}`,
                borderRadius: "5px",
                px: "6px",
                py: "2px",
              }}
            >
              ESC
            </Box>
          )}
        </Box>

        <PaletteResults
          listRef={listRef}
          rows={rows}
          active={activeIdx}
          query={q}
          error={error}
          loading={loading}
          isEmpty={isEmpty}
          showTooShortHint={showTooShortHint}
          onActivate={setActiveIdx}
          onRun={runAt}
        />

        {/* Footer keeps the keyboard contract visible, and doubles as the result
            count so a capped list still reports the true total. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            p: "8px 14px",
            borderTop: `1px solid ${tokens.border}`,
            background: tokens.surface2,
            fontSize: 11,
            color: tokens.text3,
          }}
        >
          <Box component="span" sx={{ display: "flex", gap: 1.5 }}>
            <span>↑↓ navigate</span>
            <span>↵ open</span>
            <span>esc close</span>
          </Box>
          {q && totalResults > 0 ? (
            <span>
              {totalResults} {totalResults === 1 ? "result" : "results"}
            </span>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

CommandPalette.propTypes = { open: PropTypes.bool, onClose: PropTypes.func };

export default CommandPalette;
